import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useNavigate, useParams } from 'react-router';

import Icon from '../../components/Icon';
import BackdropPage from '../account/BackdropPage';
import { CARD_INK } from '../account/backdrop';
import EventTicket, { PHONE_GUTTER } from '../explore/EventTicket';
import { ApiError } from '../../shared/api';
import {
  compareOpportunities,
  eventUrl,
  localDate,
  startMinute,
  wallClock,
  type Opportunity,
} from '../../shared/opportunity';
import { isPastEvent, newYorkNow } from '../../shared/pastEvents';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { formatTime, longDate } from '../../shared/taxonomy';
import { useCollections } from '../../shared/useCollections';
import { useHiddenEvents } from '../../shared/useHiddenEvents';
import { useOpportunityDirectory } from '../../shared/useOpportunityDirectory';
import { getSharedSchedule, type SharedSchedule } from '../account/accountApi';

/**
 * A shared schedule, at /s/:token (D11). Public: anyone with the link.
 *
 * The owner's first name, their Climate Week events day by day, and their
 * personal events as grey Busy blocks with times only. Each event has "Add to
 * my schedule", which saves to the viewer's own schedule, account or device.
 * Nothing else about the owner reaches this page.
 *
 * A list of its own rather than My Schedule's week and day views: those are
 * built around editing (dragging, trimming, travel, the callout), and making
 * them read-only would mean deep changes to Schedule and DayDetail for a page
 * whose one job is "what are they going to, and do I want to go too".
 */

type Load =
  | { kind: 'loading' }
  | { kind: 'ready'; shared: SharedSchedule }
  | { kind: 'gone' }
  | { kind: 'failed' };

interface Busy {
  dayKey: string;
  start: number;
  end: number;
  /** Ends on a later day than it starts. */
  overnight: boolean;
}

type Entry = { kind: 'event'; event: Opportunity; sort: number } | { kind: 'busy'; busy: Busy; sort: number };

const NO_EVENTS: Opportunity[] = [];

/* ── Parts ──────────────────────────────────────────────────────────────── */

/**
 * The page's own header, on the backdrop: the lockup and the one link a
 * stranger needs. `currentColor` throughout, because `BackdropPage` walks the
 * ink from off-black to parchment as the picture behind it turns to flat green
 * (Sept 19) — the top of this page is a near-white sky, where parchment is
 * invisible.
 */
const Top = styled('header')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  maxWidth: 720,
  margin: '0 auto',
  padding: '22px 16px 10px',
  boxSizing: 'border-box',
  [theme.breakpoints.down('sm')]: { padding: '16px 16px 6px' },
}));

const Home = styled(Link)(({ theme }) => ({
  display: 'block',
  width: 124,
  color: 'inherit',
  '&:hover': { opacity: 0.8 },
  [theme.breakpoints.down('sm')]: { width: 104 },
}));

const TopLink = styled(Link)({
  fontFamily: FONTS.display,
  fontWeight: 700,
  fontSize: 14,
  color: 'inherit',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  '&:hover': { opacity: 0.75 },
});

const Column = styled('main')({
  position: 'relative',
  maxWidth: 720,
  margin: '0 auto',
  // The phone list's own inset: half of each topic note hangs past the ticket,
  // and this is the room it hangs into (`EventTicket`).
  padding: `0 ${PHONE_GUTTER}px 160px`,
  boxSizing: 'border-box',
});

/**
 * Whose schedule this is, in the band between the header and the first card.
 * Out of the flow so the first card can keep its own place: its top has to
 * land exactly where the account page's first card does (§8), and a name long
 * enough to wrap must not push it down the hill.
 */
const Masthead = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: 78,
  left: PHONE_GUTTER,
  right: PHONE_GUTTER,
  color: CARD_INK,
  [theme.breakpoints.down('sm')]: { top: 62 },
}));

const Heading = styled('h1')(({ theme }) => ({
  margin: '0 0 6px',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 36,
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
  [theme.breakpoints.down('sm')]: { fontSize: 28 },
}));

const Lede = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: 15,
  lineHeight: 1.5,
  color: 'rgba(54,47,42,0.78)',
});

/**
 * How far the first day's date sits above the first ticket: its own line plus
 * the gap under it. The padding below subtracts it so that the *ticket* lands
 * on `--first-top`, which is where the account page's first card sits (§8) —
 * the date rides in the band of grass above it, as the account page's own
 * header does.
 */
const FIRST_DATE_LEAD = 45;

/**
 * The day list proper, pinned to the background's anchor like the account
 * card. Parchment from here down: the page's own ink is off-black for the sky
 * at the top, and everything below the anchor sits on grass.
 */
const Days = styled('div')({
  paddingTop: `calc(var(--first-top, 33vh) - ${FIRST_DATE_LEAD}px)`,
  color: BRAND.parchment,
  '& > section:first-of-type > h2': { marginTop: 0 },
});

const DayHeader = styled('h2')({
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  margin: '30px 0 12px',
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: 22,
  '& span': { fontFamily: FONTS.body, fontWeight: 400, fontSize: 15, color: 'rgba(243,238,231,0.7)' },
});

const List = styled('ol')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

/** Over already: dimmed, as the app dims a past card. */
const Row = styled('li')({
  '&[data-past="true"]': { opacity: 0.6 },
});

const Add = styled('button')({
  appearance: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 7,
  minHeight: 34,
  padding: '0 14px',
  borderRadius: 999,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  fontFamily: FONTS.body,
  fontSize: 12.5,
  fontWeight: 700,
  border: `1.5px solid ${BRAND.mocha}`,
  background: BRAND.mocha,
  color: BRAND.parchment,
  transition: 'background 120ms ease, color 120ms ease',
  '&:hover': { background: BRAND.mochaDeep },
  '&[aria-pressed="true"]': { background: 'transparent', color: BRAND.mocha },
  '&[aria-pressed="true"]:hover': { background: 'rgba(54,47,42,0.08)' },
});

/** A personal event of the owner's: when they're taken, and nothing more. */
const BusyBlock = styled('li')({
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  padding: '11px 18px',
  borderRadius: 10,
  background: BRAND.muted,
  color: BRAND.mocha,
  fontFamily: FONTS.body,
  fontSize: 13.5,
  '& strong': { fontFamily: FONTS.display, fontWeight: 700, fontSize: 15 },
});

const Empty = styled('p')({
  margin: '4px 0 40px',
  padding: '14px 18px',
  borderRadius: 12,
  background: 'rgba(243,238,231,0.85)',
  fontFamily: FONTS.body,
  fontSize: 15,
  color: CARD_INK,
});

const Center = styled('div')({
  maxWidth: 440,
  margin: '14vh auto 0',
  padding: '0 16px',
  textAlign: 'center',
  // High on the page, where the picture is still near-white sky.
  color: CARD_INK,
});

const Primary = styled(Link)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 48,
  marginTop: 22,
  padding: '0 24px',
  borderRadius: 999,
  fontFamily: FONTS.body,
  fontSize: 15.5,
  fontWeight: 700,
  textDecoration: 'none',
  background: BRAND.parchment,
  color: BRAND.mocha,
  '&:hover': { background: '#fff' },
});

const Retry = styled('button')({
  appearance: 'none',
  minHeight: 48,
  marginTop: 22,
  padding: '0 24px',
  borderRadius: 999,
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 15.5,
  fontWeight: 700,
  background: BRAND.parchment,
  color: BRAND.mocha,
});

/** Once something has been added: the way to where it went. */
const Tray = styled('div')({
  position: 'fixed',
  left: 16,
  right: 16,
  bottom: 16,
  maxWidth: 560,
  margin: '0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 10px 10px 18px',
  borderRadius: 999,
  background: '#1f1b18',
  boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
  fontFamily: FONTS.body,
  fontSize: 14,
  '& button': {
    appearance: 'none',
    border: 'none',
    borderRadius: 999,
    minHeight: 38,
    padding: '0 16px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: FONTS.body,
    fontSize: 13.5,
    fontWeight: 700,
    background: BRAND.parchment,
    color: BRAND.mocha,
  },
});

const Check = () => (
  <svg width={12} height={12} viewBox="0 0 14 14" aria-hidden="true" focusable="false">
    <path d="M2.5 7.4 5.6 10.4 11.6 3.6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Plus = () => (
  <svg width={11} height={11} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M11 4a1 1 0 1 1 2 0v7h7a1 1 0 1 1 0 2h-7v7a1 1 0 1 1-2 0v-7H4a1 1 0 1 1 0-2h7V4Z" />
  </svg>
);

/* ── Helpers ────────────────────────────────────────────────────────────── */

/** A Busy block's UTC instants, read on New York's clock as every event is. */
function toBusy({ startsAt, endsAt }: { startsAt: string; endsAt: string }): Busy | null {
  if (Number.isNaN(Date.parse(startsAt)) || Number.isNaN(Date.parse(endsAt))) return null;
  const start = wallClock(startsAt);
  const end = wallClock(endsAt);
  return { dayKey: start.dayKey, start: start.minutes, end: end.minutes, overnight: end.dayKey !== start.dayKey };
}

function heading(first: string | null): string {
  const name = first?.trim();
  return name ? `${name}’s Climate Week schedule` : 'A Climate Week schedule';
}

/* ── The list ───────────────────────────────────────────────────────── */

/**
 * Mounted once the schedule has arrived, so the directory knows from its
 * first render which events it is waiting for, and the list never flashes
 * empty while they load.
 */
function SharedDays({ shared }: { shared: SharedSchedule }) {
  const navigate = useNavigate();
  const directory = useOpportunityDirectory(shared.going, NO_EVENTS);
  const collections = useCollections();
  const hidden = useHiddenEvents();
  /** Added here, this visit: what the tray counts. */
  const [added, setAdded] = useState<Set<string>>(() => new Set());
  const now = useMemo(() => newYorkNow(), []);

  const days = useMemo(() => {
    const byDay = new Map<string, Entry[]>();
    const push = (day: string, entry: Entry) => {
      const list = byDay.get(day);
      if (list) list.push(entry);
      else byDay.set(day, [entry]);
    };
    for (const id of shared.going) {
      const event = directory.byId.get(id);
      // A listing since removed has no record; there is nothing to show for it.
      if (!event) continue;
      push(localDate(event), { kind: 'event', event, sort: event.isAllDay ? -1 : (startMinute(event) ?? 0) });
    }
    // One Busy block per time. Personal events at the same times (one event
    // merged in from two devices, say) would read alike, and share the key each
    // block is drawn with below, which leaves React drawing stray copies.
    const listed = new Set<string>();
    for (const raw of shared.busy) {
      const busy = toBusy(raw);
      const key = busy ? `busy-${busy.dayKey}-${busy.start}-${busy.end}` : null;
      if (key !== null && listed.has(key)) continue;
      if (key !== null) listed.add(key);
      if (busy) push(busy.dayKey, { kind: 'busy', busy, sort: busy.start });
    }
    return [...byDay.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([dayKey, entries]) => ({
        dayKey,
        entries: entries.sort((a, b) =>
          a.sort !== b.sort
            ? a.sort - b.sort
            : a.kind === 'event' && b.kind === 'event'
              ? compareOpportunities(a.event, b.event)
              : a.kind === 'event'
                ? -1
                : 1,
        ),
      }));
  }, [shared, directory.byId]);

  const toggle = (event: Opportunity) => {
    const on = collections.isScheduled(event.id);
    // Hidden from their Explore, but wanted now: it can't be both.
    if (!on && hidden.isHidden(event.id)) hidden.unhide(event.id);
    // 'card' until telemetry has a surface of its own for this page.
    collections.toggleScheduled(event.id, 'card', event.name);
    setAdded((prev) => {
      const next = new Set(prev);
      if (on) next.delete(event.id);
      else next.add(event.id);
      return next;
    });
  };

  if (!directory.ready) {
    return (
      <Center role="status" aria-label="Loading">
        <CircularProgress sx={{ color: BRAND.parchment }} />
      </Center>
    );
  }

  const stillAdded = [...added].filter((id) => collections.isScheduled(id)).length;

  return (
    <>
      {days.length === 0 && <Empty>Nothing on this schedule yet.</Empty>}

      {days.map(({ dayKey, entries }) => {
        const date = longDate(dayKey);
        return (
          <section key={dayKey} aria-label={`${date.weekday}, ${date.full}`}>
            <DayHeader>
              {date.full} <span>{date.weekday}</span>
            </DayHeader>
            <List>
              {entries.map((entry) => {
                if (entry.kind === 'busy') {
                  const { busy } = entry;
                  return (
                    <BusyBlock key={`busy-${busy.dayKey}-${busy.start}-${busy.end}`}>
                      <strong>Busy</strong>
                      {/* Written as the events' own times are. */}
                      {formatTime(busy.start)} - {formatTime(busy.end)}
                      {busy.overnight ? ' (next day)' : ''}
                    </BusyBlock>
                  );
                }
                const { event } = entry;
                const on = collections.isScheduled(event.id);
                const past = isPastEvent(event, now);
                const titleId = `shared-${event.id}`;
                return (
                  <Row key={event.id} data-past={past}>
                    {/* The app's own phone ticket, so a shared schedule reads
                        as the list it was saved from (Sam, Sept 19). The push
                        that moves it between lists is not here: a stranger's
                        page gets a button they can see. */}
                    <EventTicket
                      event={event}
                      scheduled={on}
                      past={past}
                      titleHref={eventUrl(event)}
                      id={titleId}
                      action={
                        /* Over already: nothing to add it for. */
                        past ? undefined : (
                          <Add
                            type="button"
                            aria-pressed={on}
                            aria-describedby={titleId}
                            onClick={() => toggle(event)}
                          >
                            {on ? <Check /> : <Plus />}
                            {on ? 'On my schedule' : 'Add to my schedule'}
                          </Add>
                        )
                      }
                    />
                  </Row>
                );
              })}
            </List>
          </section>
        );
      })}

      {stillAdded > 0 && (
        <Tray role="status">
          <span>
            {stillAdded} {stillAdded === 1 ? 'event' : 'events'} added to your schedule
          </span>
          <button type="button" onClick={() => navigate('/', { state: { view: 'schedule' } })}>
            See My Schedule
          </button>
        </Tray>
      )}
    </>
  );
}

/* ── The page ───────────────────────────────────────────────────────────── */

export default function SharedSchedulePage() {
  const { token = '' } = useParams();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    getSharedSchedule(token)
      .then((shared) => {
        if (!cancelled) setLoad({ kind: 'ready', shared });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoad(err instanceof ApiError && err.status === 404 ? { kind: 'gone' } : { kind: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [token, attempt]);

  const title = load.kind === 'ready' ? heading(load.shared.ownerFirstName) : 'A Climate Week schedule';

  let body;
  if (load.kind === 'loading') {
    body = (
      <Center role="status" aria-label="Loading">
        <CircularProgress sx={{ color: BRAND.parchment }} />
      </Center>
    );
  } else if (load.kind === 'gone') {
    body = (
      <Center>
        <Heading style={{ margin: 0 }}>This link has been turned off</Heading>
        <Lede style={{ marginTop: 12 }}>
          Whoever shared it has turned it off or replaced it with a new one. Ask them for the latest link, or plan
          your own week.
        </Lede>
        <Primary to="/">Explore Climate Week events</Primary>
      </Center>
    );
  } else if (load.kind === 'failed') {
    body = (
      <Center>
        <Heading style={{ margin: 0 }}>We couldn&rsquo;t load this schedule</Heading>
        <Lede style={{ marginTop: 12 }}>Check your connection and try again.</Lede>
        <Retry type="button" onClick={() => setAttempt((n) => n + 1)}>
          Try again
        </Retry>
      </Center>
    );
  } else {
    body = (
      <Column>
        <Masthead>
          <Heading>{title}</Heading>
          <Lede>Shared from My Climate Week. Add anything that catches your eye to your own schedule.</Lede>
        </Masthead>
        <Days>
          <SharedDays shared={load.shared} />
        </Days>
      </Column>
    );
  }

  return (
    <BackdropPage
      header={
        <Top>
          <Home to="/" aria-label="My Climate Week — all events">
            <Icon name="logo:mcw" size="100%" />
          </Home>
          <TopLink to="/">Plan your own week →</TopLink>
        </Top>
      }
    >
      {/* React puts these in <head>: a shared schedule is nobody's search result. */}
      <title>{`${title} · My Climate Week`}</title>
      <meta name="robots" content="noindex, nofollow" />

      {body}
    </BackdropPage>
  );
}
